import { memo } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useImagePreloader from "../hooks/useImagePreloader";
import LoadingOverlay from "../components/LoadingOverlay";

const PolicySection = memo(({ title, content, items }) => (
  <section>
    <h2 className="text-xl font-semibold text-[#009fd6] mb-4">{title}</h2>
    {content &&
      content.map((paragraph, index) => (
        <p key={index} className="mb-4">
          {paragraph}
        </p>
      ))}
    {items && (
      <ul className="list-disc pl-6 mb-4 space-y-2">
        {items.map((item, index) => (
          <li key={index}>
            <strong>{item.key}:</strong> {item.value}
          </li>
        ))}
      </ul>
    )}
  </section>
));

PolicySection.displayName = "PolicySection";

PolicySection.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.arrayOf(PropTypes.string),
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
};

function CookiePolicy() {
  useDocumentTitle("cookiePolicy");
  const { t } = useTranslation();

  const cookiePolicyData = t("components.cookiePolicy", {
    returnObjects: true,
  });

  const loading = useImagePreloader([]);

  const pageAnimation = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1,
      },
    },
  };

  return (
    <>
      {loading && <LoadingOverlay />}
      <div className="bg-black text-gray-200 pt-24 pb-16 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={pageAnimation}
            aria-label="Cookie Policy Content"
          >
            <h1 className="text-3xl font-bold text-white mb-8">
              {cookiePolicyData.title}
            </h1>

            <div className="space-y-8">
              <PolicySection
                title={cookiePolicyData.sections.introduction.title}
                content={cookiePolicyData.sections.introduction.content}
              />

              <PolicySection
                title={cookiePolicyData.sections.whatAreCookies.title}
                content={cookiePolicyData.sections.whatAreCookies.content}
              />

              <PolicySection
                title={cookiePolicyData.sections.typesOfCookies.title}
                items={
                  cookiePolicyData.sections.typesOfCookies.content[0].items
                }
              />

              <PolicySection
                title={cookiePolicyData.sections.managingPreferences.title}
                content={cookiePolicyData.sections.managingPreferences.content}
              />

              <PolicySection
                title={cookiePolicyData.sections.changesToPolicy.title}
                content={cookiePolicyData.sections.changesToPolicy.content}
              />

              <PolicySection
                title={cookiePolicyData.sections.contactUs.title}
                content={cookiePolicyData.sections.contactUs.content}
              />
            </div>

            <div className="border-t border-gray-700 pt-6 mt-8">
              <p className="text-sm text-gray-400">
                {cookiePolicyData.lastUpdated}
              </p>
            </div>

            <div className="mt-12 flex justify-center">
              <Link to="/">
                <button
                  className="bg-[#009fd6] text-white px-6 py-3 rounded-lg hover:bg-[#007cab] transition-colors"
                  aria-label="Return to Home"
                >
                  {cookiePolicyData.returnToHome}
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default CookiePolicy;
